import { Injectable } from '@angular/core';
import {
  EsriGraphic,
  EsriSimpleFillSymbol,
  EsriSimpleLineSymbol,
  EsriSimpleMarkerSymbol,
} from 'src/app/config/esri.module';
import { ConfigService } from '../../config/config.service';
import { CONSTANTS_VALUE } from '../../config/constants.module';
import { BottomPanelService } from '../../bottom-panel/services/bottom-panel.service';
import { MainMapService } from './main-map.service';

@Injectable({
  providedIn: 'root',
})
export class SelectionService {

  constructor(
    private configService: ConfigService,
    private mainMapService: MainMapService,
    private bottomPanelService: BottomPanelService
  ) {}

  /**
   * @name addSelectedGraphic
   * @param graphic
   * @description Adds the given graphic to the map with the selected symbol
   */
  public addSelectedGraphic(graphic: __esri.Graphic) {
    // create clone with selected symbol
    if (!graphic.geometry) return;
    const clone = graphic.clone();
    clone.symbol = this.createSelectedSymbol(clone.geometry.type);
    // add to map
    this.mainMapService.mapView.graphics.add(clone);
  }

  /**
   * @name removeSelectedGraphic
   * @param graphic
   * @description Removes the given graphic from the map
   */
  public removeSelectedGraphic(graphic: __esri.Graphic) {
    if (!graphic) {
      return;
    }
    this.mainMapService.mapView.graphics.filter(mapGraphic => {
      if (
        mapGraphic.attributes &&
        mapGraphic.attributes.OBJECTID !== graphic.attributes.OBJECTID &&
        mapGraphic['sourceLayer'] === graphic['sourceLayer']
      ) {
        this.mainMapService.mapView.graphics.remove(mapGraphic);
        return true;
      } else if (mapGraphic.attributes && mapGraphic.attributes.OBJECTID === graphic.attributes.OBJECTID) {
        this.mainMapService.mapView.graphics.remove(mapGraphic);
        return true;
      }
    });
  }

  /**
   * @name removeAllSelectedGraphics
   * @description Removes all selected grpahics from the map except FlashPoint symbols from GoTo-Widget
   */
  public removeAllSelectedGraphics() {
    // iterate over all temp graphics
    this.mainMapService.mapView.graphics.forEach(graphic => {
      if (
        (graphic.attributes &&
          (graphic.attributes.name === CONSTANTS_VALUE.GRAPHIC_TYPE.FLASH_POINT ||
            graphic.attributes.name === CONSTANTS_VALUE.GRAPHIC_TYPE.IMMORAIL_POINT)) ||
        graphic.symbol['id'] === CONSTANTS_VALUE.GRAPHIC_TYPE.ESRi_SEARCH_POINT
      ) {
        return;
      }
      this.mainMapService.mapView.graphics.remove(graphic);
    });
  }

  // -------- Zoom / GoTo -------------

  public async goToFeature(feature: __esri.Graphic, options?: __esri.GoToOptions2D, scale?: number) {
    const heightOfList = this.bottomPanelService.heightBottomPanel || 0; // HOTFIX: heightBottomPanel not set at app start
    const deltaY = this.mainMapService.mapView.container.offsetHeight;
    const visibleMapOnOpenList = deltaY - heightOfList;

    const heightOfSelectedFeature = feature.geometry.extent ? feature.geometry.extent.height : 1;

    const invisibleMap = (heightOfSelectedFeature * heightOfList) / visibleMapOnOpenList + 50;

    const selectedFeatureExtent = feature.geometry.extent;
    if (selectedFeatureExtent) {
      selectedFeatureExtent.ymax +=
        heightOfList > CONSTANTS_VALUE.UI.bottomListSmallHeight ? invisibleMap : invisibleMap * 1.5;
      selectedFeatureExtent.ymin -= invisibleMap / 2;
      selectedFeatureExtent.offset(0, -invisibleMap, 0);

      await this.mainMapService.mapView.when(() => {});
      await this.mainMapService.mapView.goTo(selectedFeatureExtent, { animate: true });
    } else {
      // Workaround for points
      const point = feature.geometry as __esri.Point;

      await this.mainMapService.mapView.when(() => {});
      await this.mainMapService.mapView.goTo({
        center: point,
        scale: scale,
      });
    }
  }

  /**
   * @function zoomOnFeature
   * @param feature
   * @description Zoom auf  eine bestimmte feature object in der Karte
   */
  public zoomOnFeature(feature): void {
    if (!feature.geometry) return;
    this.goToFeature(feature, { animate: false }, 1000);
  }


  /**
   * @name setSearchMarker
   * @param point
   * @description create marker for search and added in map
   */
  public setSearchMarker(point: __esri.Point) {
    if (!point) {
      return;
    }
    // create search graphic
    const symbol: __esri.SimpleMarkerSymbol = new EsriSimpleMarkerSymbol(
      this.configService.mainMapConfig.searchMarker.symbol
    );
    let searchGraphic: __esri.Graphic;
    this.mainMapService.mapView.graphics.removeAll();
    const graphicProperties: __esri.GraphicProperties = {
      geometry: point,
      symbol: symbol,
      attributes: {
        name: CONSTANTS_VALUE.GRAPHIC_TYPE.IMMORAIL_POINT,
      },
    };
    searchGraphic = new EsriGraphic(graphicProperties);
    this.mainMapService.mapView.graphics.add(searchGraphic);
    // remove Immorail jump point graphic after 20 seconds from the mapview
    setTimeout(() => {
      this.mainMapService.mapView.graphics.remove(searchGraphic);
    }, CONSTANTS_VALUE.SEARCH_GRAPHIC_TIMEOUT.MINUTES_2);
  }

  /**
   * @name createSelectedSymbol
   * @param geometryType GeometryType
   * @returns {__esri.Symbol}
   * @description Creates a selected Symbol based on the geometry-type
   */
  private createSelectedSymbol(geometryType: string): __esri.SymbolUnion {
    switch (geometryType) {
      case 'point':
        return new EsriSimpleMarkerSymbol(this.configService.mainMapConfig.select.symbols.point);
      case 'polyline':
        return new EsriSimpleLineSymbol(this.configService.mainMapConfig.select.symbols.polyline);
      case 'polygon':
        return new EsriSimpleFillSymbol(this.configService.mainMapConfig.select.symbols.polygon);
      default:
        throw new Error('not supported');
    }
  }
}
